import React, { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import {
	BUTTON_TYPES,
	EvMainTitle,
	EvSection,
	EvSpinner,
	OptionType,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { EditViewButton } from '../../components/edit-view-popup/EditViewButton';
import { ManageViewsTable } from '../../components/manage-views-table/ManageViewsTable';
import { SearchFilter } from '../../components/search-filter/SearchFilter';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { withLimitedAccess } from '../../hoc/withLimitedAccess';
import { IScanConfiguration } from '../../interfaces/ScanConfiguration';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { useNavigation } from '../../providers/navigationProvider/NavigationProvider';
import { useNotifications } from '../../providers/notificationsProvider/NotificationsConsumer';
import { INotificationsContext } from '../../providers/notificationsProvider/NotificationsContext';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { getProperty } from '../../services/PropertiesService';
import { getIssueTypesList } from '../../services/PropertyViewsService';
import { getCustomSeverityOptionsList } from '../../services/severities/SeverityService';

import './ManageViewsPage.scss';

const MANAGE_VIEWS_PAGE_TITLE = 'Manage Views';

const ManageViewsPage: FC = () => {
	const { propertyId } = useParams<{ propertyId: string }>();

	const [searchFilterValue, setSearchFilterValue] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [issueTypesList, setIssueTypesList] = useState<OptionType[]>([]);
	const [issueSeveritiesList, setIssueSeveritiesList] = useState<OptionType[]>([]);
	const [isIssueListsLoading, setIsIssueListsLoading] = useState<boolean>(true);
	const [customSeverityNames, setCustomSeverityNames] = useState<
		IScanConfiguration['customSeverityNames']
	>({});

	const notificationsContext: INotificationsContext = useNotifications();
	const { setCurrentRoute } = useNavigation();
	const { getToggle, configuration } = useConfiguration();

	const isSeverityFilterEnabled = getToggle(SiteScannerUiToggles.ENABLE_SEVERITY_VIEW_FILTER);
	const severitiesDisplayMode = configuration?.CONFIG?.VIEW_SEVERITIES_DISPLAY_MODE;

	const defaultErrorHandler = useCallback(
		(error: Error, announcement: string): void => {
			notificationsContext.toast({
				show: true,
				status: TOAST_STATUS.FAIL,
				announcement
			});
			logger.error(announcement, error);
		},
		[notificationsContext]
	);

	useEffect(() => {
		setIsLoading(true);
		getProperty(propertyId)
			.then((property) => {
				setCustomSeverityNames(property.scanConfiguration.customSeverityNames ?? {});
				setCurrentRoute({
					id: `${MANAGE_VIEWS_PAGE_TITLE}-${propertyId}`,
					url: RoutesHelper.getManageViewsPath(propertyId),
					title: MANAGE_VIEWS_PAGE_TITLE
				});
			})
			.catch((err) => {
				logger.error('ManageViewsPage failed to load property', err);
			})
			.finally(() => {
				setIsLoading(false);
			});

		return () => {
			notificationsContext.toast({ show: false });
			setCurrentRoute(null);
		};
	}, [propertyId, notificationsContext, setCurrentRoute]);

	useEffect(() => {
		const fetchIssueTypesList = async (): Promise<void> => {
			try {
				const issueTypesList = await getIssueTypesList();
				const issueTypeOptionsList = issueTypesList.map((issueType) => {
					return { label: issueType, value: issueType };
				});

				setIssueTypesList(issueTypeOptionsList);
			} catch (error) {
				defaultErrorHandler(error, 'Couldn`t load issues types list');
			}
		};

		if (isSeverityFilterEnabled) {
			const severityOptionsList = getCustomSeverityOptionsList(
				customSeverityNames,
				severitiesDisplayMode
			);

			setIssueSeveritiesList(severityOptionsList);
		}

		setIsIssueListsLoading(true);
		fetchIssueTypesList();
		setIsIssueListsLoading(false);
	}, [defaultErrorHandler, isSeverityFilterEnabled, severitiesDisplayMode, customSeverityNames]);

	return isLoading || isIssueListsLoading ? (
		<EvSpinner />
	) : (
		<div className="manage-views-page">
			<div className="manage-views-page-header">
				<EvMainTitle className="page-heading">{MANAGE_VIEWS_PAGE_TITLE}</EvMainTitle>
				<div className="add-view-actions">
					<EditViewButton
						type={BUTTON_TYPES.PRIMARY}
						propertyId={propertyId}
						innerText="Create View"
						issueTypesList={issueTypesList}
						issueSeveritiesList={issueSeveritiesList}
					/>
				</div>
			</div>

			<SearchFilter searchInputValue={searchFilterValue} onInputChange={setSearchFilterValue} />

			<EvSection ariaLabel="Manage Views table">
				<ManageViewsTable
					searchFilter={searchFilterValue}
					issueTypesList={issueTypesList}
					issueSeveritiesList={issueSeveritiesList}
				/>
			</EvSection>
		</div>
	);
};

export const ManageViewsPageWithAccess = withLimitedAccess(ManageViewsPage);
